import { useEffect, useState } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useRecurringSettings } from "@circle-react/components/Events/DetailsForm/EventTimeLocation/hooks/useRecurringSettings";
import { RECURRING_ENDING } from "@circle-react/components/Events/helpers/constants";
import {
  browserTimeZone,
  getTimezoneShort,
} from "@circle-react/helpers/dateTimeHelpers";
import { useEventsFeatureFlags } from "@circle-react/hooks/useEventsFeatureFlags";
import type { Event } from "@circle-react/types";
import { DateRangePicker } from "@circle-react-uikit/DateRangePicker";
import { DateTimePicker } from "@circle-react-uikit/DateTimePicker";
import { Form } from "@circle-react-uikit/Form";
import { Typography } from "@circle-react-uikit/Typography";
import { FormItem } from "../../../Common";
import { WarningAlert } from "../../../Common/WarningAlert";
import {
  hasEventEnded,
  isDraft,
  isPublished,
  isRecurring,
} from "../../../utils";

interface DateAndTimeProps {
  event: Event;
}

export const DateAndTime = ({ event }: DateAndTimeProps) => {
  const {
    endsAtMaxDate,
    endsAfterMaxOccurrences,
    frequencyOptions,
    endingOptions,
    endsAtMinDate,
    endsOnValue,
    shouldShowRecurringFields,
    currentEndsAt,
    startsAtInUTC,
  } = useRecurringSettings({ event });

  const { hasSplitRecurringEventsEnabled } = useEventsFeatureFlags();

  const isDateFieldDisabled = event && hasEventEnded(event);

  const { setValue, getValues, clearErrors } = useFormContext();
  const { isSubmitting, isSubmitted } = useFormState();

  const [submittedFrequency, setSubmittedFrequency] = useState<string | null>(
    null,
  );
  const [shouldShowWarning, setShouldShowWarning] = useState(false);
  const frequency = getValues("recurring_setting_attributes.frequency");

  // Check if startsAtInUTC is greater than currentEndsAt and show warning
  useEffect(() => {
    if (startsAtInUTC && currentEndsAt && isRecurring(event)) {
      const startsAtDate = new Date(startsAtInUTC);
      const endsAtDate = new Date(currentEndsAt);

      if (startsAtDate > endsAtDate) {
        setShouldShowWarning(true);
        clearErrors("event_setting_attributes.starts_at");
      } else {
        setShouldShowWarning(false);
        clearErrors("event_setting_attributes.starts_at");
      }
    } else {
      setShouldShowWarning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clearErrors is a stable utility function from React Hook Form
  }, [startsAtInUTC, currentEndsAt]);

  useEffect(() => {
    if (isSubmitting) {
      setSubmittedFrequency(frequency);
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (!isSubmitting && !isSubmitted) {
      setSubmittedFrequency(null);
    }
  }, [isSubmitted, isSubmitting, submittedFrequency]);

  useEffect(() => {
    if (submittedFrequency && isSubmitted) {
      setValue("recurring_setting_attributes.frequency", submittedFrequency, {
        shouldDirty: false,
      });
    }
  }, [submittedFrequency, frequency, isSubmitted, setValue]);

  // Note: We should remove isDraft(event) once we can change recurring settings on
  // draft events. Leaving shouldDisableRecurringFields as a placeholder for now that will
  // serve as the condition for disabling recurring event fields.
  const shouldDisableRecurringEventFields =
    (isDraft(event) || isPublished(event)) && isRecurring(event);

  // Frequency field should be disabled when editing existing event (original behavior), or when feature flag is enabled and conditions are met
  const isFrequencyFieldDisabled = !hasSplitRecurringEventsEnabled
    ? !!event
    : (!!event && !isRecurring(event)) || isDateFieldDisabled;

  return (
    <div className="my-6 md:mb-8 md:mt-16">
      <Typography.TitleMd>
        {t("events.create.form.date_and_time")}
      </Typography.TitleMd>
      <div className="flex flex-col items-stretch md:flex-row">
        <div className="w-full">
          <FormItem
            name="event_setting_attributes.starts_at"
            label={t("events.create_modal.event_setting_attributes.starts_at", {
              timeZone: getTimezoneShort(browserTimeZone),
            })}
            translationRoot="events.create.form"
          >
            <DateRangePicker
              startsAtConfig={{
                name: "event_setting_attributes.starts_at",
                minDate: new Date(),
              }}
              endsAtConfig={{ name: "event_setting_attributes.ends_at" }}
              durationConfig={{
                name: "event_setting_attributes.duration_in_seconds",
              }}
              disabled={isDateFieldDisabled}
            />
          </FormItem>
        </div>
      </div>

      {shouldShowWarning && (
        <WarningAlert
          title={t(
            "events.edit_modal.recurring_setting_attributes.starts_at_after_ends_at_warning_title",
          )}
          description={t(
            "events.edit_modal.recurring_setting_attributes.starts_at_after_ends_at_warning_content",
          )}
        />
      )}

      <div className="flex items-stretch">
        <div className="w-full pr-0 md:w-1/2 md:pr-2">
          <FormItem
            name="recurring_setting_attributes.frequency"
            translationRoot="events.create.form"
          >
            <Form.AnimatedSelect
              name="recurring_setting_attributes.frequency"
              fullWidth
              options={frequencyOptions}
              disabled={isFrequencyFieldDisabled}
              dataTestId="event-repeats"
            />
          </FormItem>
        </div>
      </div>

      <div className="flex flex-col items-stretch md:flex-row">
        {shouldShowRecurringFields && (
          <div className="w-full pr-0 md:w-1/2 md:pr-2">
            <FormItem
              name="recurring_setting_attributes.range_type"
              translationRoot="events.create.form"
            >
              <Form.AnimatedSelect
                name="recurring_setting_attributes.range_type"
                fullWidth
                options={endingOptions}
                disabled={shouldDisableRecurringEventFields}
                dataTestId="event-ends"
              />
            </FormItem>
          </div>
        )}
        {shouldShowRecurringFields && (
          <div className="w-full pl-0 md:w-1/2 md:pl-2">
            {(!endsOnValue || endsOnValue === RECURRING_ENDING.ON) && (
              <div className="mt-0 flex items-end md:mt-7">
                <FormItem
                  name="recurring_setting_attributes.ends_at"
                  hideLabel
                  rules={{
                    required: t("events.create.form.ends_at_required"),
                  }}
                  translationRoot="events.create.form"
                >
                  <DateTimePicker
                    name="recurring_setting_attributes.ends_at"
                    minDate={
                      endsAtMinDate ? new Date(endsAtMinDate) : undefined
                    }
                    maxDate={
                      endsAtMaxDate ? new Date(endsAtMaxDate) : undefined
                    }
                    disabled={shouldDisableRecurringEventFields}
                    dataTestId="recurring_event_ends_at"
                    datePlaceholder={t(
                      "events.create.form.recurring_setting_attributes.date_placeholder",
                    )}
                    timePlaceholder={t(
                      "events.create.form.recurring_setting_attributes.time_placeholder",
                    )}
                  />
                </FormItem>
              </div>
            )}
            {endsOnValue === RECURRING_ENDING.AFTER && (
              <div className="flex items-end">
                <div className="grow">
                  <FormItem
                    name="recurring_setting_attributes.occurrences"
                    rules={{
                      required:
                        !shouldDisableRecurringEventFields &&
                        t("events.create.form.occurrences_required"),
                      max: {
                        value: endsAfterMaxOccurrences || 1,
                        message: t("events.create.form.max_occurrences_error"),
                      },
                    }}
                    translationRoot="events.create.form"
                  >
                    <Form.Input
                      type="number"
                      min={1}
                      max={endsAfterMaxOccurrences || 1}
                      autoFocus
                      disabled={shouldDisableRecurringEventFields}
                      data-testid="event-occurrences"
                      customOpacity="70"
                    />
                  </FormItem>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
